import type { BaseRecord, DataProvider, GetListResponse } from "@refinedev/core";

import { api } from "../api";

// Resources map to apps/api office endpoints: `staff` → /api/office/staff. Lists answer
// `{ data, total }` (or a plain array for small catalogs).
const API_URL = "/api/office";

type ListAnswer = { data: BaseRecord[]; total: number } | BaseRecord[];

export const dataProvider: DataProvider = {
  getApiUrl: () => API_URL,

  getList: async ({ resource, pagination, filters }) => {
    const query = new URLSearchParams();
    if (pagination?.mode === "server") {
      query.set("page", String(pagination.currentPage ?? 1));
      query.set("pageSize", String(pagination.pageSize ?? 25));
    }
    for (const filter of filters ?? []) {
      if ("field" in filter && filter.operator === "eq" && filter.value) {
        query.set(filter.field, String(filter.value));
      }
    }
    const suffix = query.size > 0 ? `?${query}` : "";
    const answer = await api<ListAnswer>(`${API_URL}/${resource}${suffix}`);
    const result = Array.isArray(answer) ? { data: answer, total: answer.length } : answer;
    // Records are typed by each screen (Refine's generic `TData`).
    return result as GetListResponse<never>;
  },

  getOne: async ({ resource, id }) => ({
    data: await api(`${API_URL}/${resource}/${encodeURIComponent(String(id))}`),
  }),

  create: async ({ resource, variables }) => ({
    data: await api(`${API_URL}/${resource}`, { method: "POST", body: variables }),
  }),

  update: async ({ resource, id, variables }) => {
    await api(`${API_URL}/${resource}/${id}`, { method: "PUT", body: variables });
    return { data: { id, ...(variables as object) } as never };
  },

  deleteOne: async ({ resource, id }) => {
    await api(`${API_URL}/${resource}/${id}`, { method: "DELETE" });
    return { data: { id } as never };
  },
};
